import {
  action,
  computed,
  IReactionDisposer,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import * as React from 'react';
import { GTMReporters } from 'components/HOCs/withGtmLogger';
import { PlanType, ProductFamily } from 'enums';
import { BaseProduct } from 'types';
import {
  PlanTypeClick,
  SelectPlanClickSource,
} from 'services/BiLogger/BiLoggerTypes';
import { PlansPageProps } from './PlansPageProps';

export const tabSwitchDelay = 250;

export const usePlansPageController = (props: PlansPageProps) => {
  const controller = React.useMemo(() => new PlansPageController(props), []);
  React.useEffect(() => {
    return () => controller.destroy();
  }, []);
  return controller;
};
class PlansPageController {
  @observable isInTransition: boolean = false;
  @observable isRequestACallModalOpened: boolean = false;
  @observable planType!: PlanType;
  reactionDisposer!: IReactionDisposer;

  constructor(private readonly props: PlansPageProps) {
    this.planType = this.props.rootStore.appStore.planType;
    this.reactionDisposer = reaction(
      () => this.props.rootStore.appStore.planType,
      (planType) => {
        this.isInTransition = true;
        setTimeout(() => {
          runInAction(() => {
            this.isInTransition = false;
            this.planType = planType;
          });
        }, tabSwitchDelay);
      },
    );
    void this.notifyGTM();
  }

  notifyGTM = async () => {
    const { reportGTM, rootStore } = this.props;
    const { storage } = rootStore.appStore;
    storage.getPPViewed().then(
      () => { },
      async () => {
        await storage.setPPViewed();
        reportGTM(GTMReporters.PackagePicker);
      },
    );
  };

  get shouldShowLimitedTimeBanner(): boolean {
    const {
      appContext: { isLimitedTimeDiscount },
      isInSale,
    } = this.props.rootStore.appStore;
    return !isInSale && isLimitedTimeDiscount && !this.isInTransition;
  }

  @action
  websiteBannerCTA = (goToPlan: PlanType) => {
    const { scrollToTop, selectTab } = this.props.rootStore.appStore;
    if (this.hasEnterpriseProduct) {
      this.isRequestACallModalOpened = true;
      return;
    }
    scrollToTop();
    selectTab(goToPlan, PlanTypeClick.Bottom);
  };

  @action
  closeRequestACallModal = () => {
    this.props.rootStore.appStore.appContext.biLogger.logRequestCallPopupCTA({
      clickType: 'close',
    });
    this.isRequestACallModalOpened = false;
  };

  @action
  onSelectProduct = (
    product: BaseProduct,
    clickSource?: SelectPlanClickSource,
  ) => {
    if (product.productFamily === ProductFamily.Enterprise) {
      this.isRequestACallModalOpened = true;
      return;
    }
    this.props.onSelectProduct(product, clickSource);
  };

  @computed get hasEnterpriseProduct() {
    const { advancedPlansProducts } = this.props.rootStore.appStore.appContext;
    return !!advancedPlansProducts.find(
      (p) =>
        p.type === PlanType.Business &&
        p.productFamily === ProductFamily.Enterprise,
    );
  }

  destroy = () => {
    this.reactionDisposer();
  };
}
